import { useState, type FormEvent } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  useAcknowledgeSmsTerms,
  useRecordSmsConsent,
  useSmsConfig,
  type SmsRegistrationStatus,
} from '@/data/sms';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import { toast } from '@/lib/toast';

// Admin SMS compliance settings. This route is admin-only (the /admin layout
// guards on the admin role). SMS is OFF by default and cannot be sent in
// production until the studio is A2P-10DLC approved AND recipients consent.
// The page is informational + a single terms-acknowledge action; admins cannot
// self-grant 'approved'.

export const Route = createFileRoute('/admin/settings/sms')({
  component: AdminSmsSettings,
});

const STATUS_LABEL: Record<SmsRegistrationStatus, string> = {
  not_configured: 'Not configured',
  awaiting_registration: 'Awaiting A2P registration',
  approved: 'Approved',
  suspended: 'Suspended',
};

const STATUS_TONE: Record<SmsRegistrationStatus, 'neutral' | 'warn' | 'success' | 'danger'> = {
  not_configured: 'neutral',
  awaiting_registration: 'warn',
  approved: 'success',
  suspended: 'danger',
};

interface ChecklistItem {
  label: string;
  done: boolean;
  detail: string;
}

function AdminSmsSettings() {
  const config = useSmsConfig();
  const acknowledge = useAcknowledgeSmsTerms();
  const recordConsent = useRecordSmsConsent();
  const [phone, setPhone] = useState('');
  const [source, setSource] = useState('');
  const [optIn, setOptIn] = useState(true);

  async function handleRecordConsent(e: FormEvent) {
    e.preventDefault();
    if (phone.trim().length < 7 || source.trim().length < 3) {
      toast({
        title: 'Phone and consent source are required',
        description: 'Enter the recipient phone and how consent was obtained.',
        variant: 'error',
      });
      return;
    }
    try {
      const status = await recordConsent.mutateAsync({ phone, optIn, source });
      const optedIn = status === 'opted_in';
      toast({
        title: optedIn ? 'Opt-in recorded' : 'Opt-out recorded',
        description: `${phone.trim()} is now ${optedIn ? 'opted in' : 'opted out'}.`,
        variant: 'success',
      });
      setPhone('');
      setSource('');
    } catch (err) {
      toast({
        title: "Couldn't record consent",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  async function handleAcknowledge() {
    try {
      await acknowledge.mutateAsync();
      toast({
        title: 'Compliance terms acknowledged',
        description: 'Recorded. SMS still requires carrier approval to send.',
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: "Couldn't record acknowledgement",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  if (config.isLoading) return <p aria-live="polite">Loading…</p>;

  if (!config.data) {
    return (
      <Empty
        title="SMS settings unavailable"
        description="Refresh and try again, or check the SMS compliance migration."
      />
    );
  }

  const c = config.data;
  const checklist: ChecklistItem[] = [
    {
      label: 'A2P 10DLC brand & campaign approved',
      done: c.registrationStatus === 'approved',
      detail:
        'Your studio must register a brand and messaging campaign with the carriers (A2P 10DLC). This is approved out-of-band by Pirouette once the carrier clears it.',
    },
    {
      label: 'Compliance terms acknowledged',
      done: c.termsAcknowledgedAt != null,
      detail: 'An admin attests to the SMS messaging obligations (consent, opt-out, content).',
    },
    {
      label: 'SMS channel enabled',
      done: c.smsEnabled,
      detail: 'The SMS sending entitlement is switched on for your studio.',
    },
    {
      label: 'At least one consenting recipient',
      done: c.optedInCount > 0,
      detail: `${c.optedInCount} number(s) have opted in. Recipients opt in explicitly; texting STOP opts them out.`,
    },
  ];

  const blockers = checklist.filter((item) => !item.done);

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Settings
        </p>
        <h1 className="mt-2">SMS messaging</h1>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Badge tone={STATUS_TONE[c.registrationStatus]}>
            {STATUS_LABEL[c.registrationStatus]}
          </Badge>
          <Badge tone={c.canSend ? 'success' : 'neutral'}>
            {c.canSend ? 'Ready to send' : 'Sending disabled'}
          </Badge>
        </div>
        <p className="mt-3 max-w-prose text-[var(--color-ink-muted)]">
          SMS is disabled by default. Messages can only be sent once your studio is approved for A2P
          10DLC and the recipient has opted in. Until then, notifications fall back to email and
          in-app delivery.
        </p>
      </header>

      {!c.canSend && (
        <section
          aria-labelledby="why-heading"
          className="surface-elevated space-y-3 p-6"
          role="status"
        >
          <h2 id="why-heading" className="font-[var(--font-display)] text-[var(--text-xl)]">
            Why SMS is unavailable
          </h2>
          <p className="text-[var(--color-ink-muted)]">
            The following must be in place before any text message can be sent:
          </p>
          <ul className="list-disc space-y-1 pl-5 text-[var(--color-ink-muted)]">
            {blockers.map((item) => (
              <li key={item.label}>{item.label}</li>
            ))}
          </ul>
          <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            In the meantime, time-critical notifications are delivered by email and in-app. No
            action from families is lost.
          </p>
        </section>
      )}

      <section aria-labelledby="checklist-heading" className="surface-elevated space-y-4 p-6">
        <h2 id="checklist-heading" className="font-[var(--font-display)] text-[var(--text-xl)]">
          Compliance checklist
        </h2>
        <ul className="space-y-4">
          {checklist.map((item) => (
            <li key={item.label} className="flex items-start gap-3">
              <Badge tone={item.done ? 'success' : 'neutral'}>
                {item.done ? 'Done' : 'Pending'}
              </Badge>
              <div>
                <p className="font-medium">{item.label}</p>
                <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">{item.detail}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="terms-heading" className="surface-elevated space-y-4 p-6">
        <header className="flex items-baseline justify-between gap-3">
          <h2 id="terms-heading" className="font-[var(--font-display)] text-[var(--text-xl)]">
            Acknowledge compliance terms
          </h2>
          <Badge tone={c.termsAcknowledgedAt ? 'success' : 'warn'}>
            {c.termsAcknowledgedAt ? 'Acknowledged' : 'Required'}
          </Badge>
        </header>
        <p className="text-[var(--color-ink-muted)]">
          By acknowledging, you confirm your studio will only text recipients who have opted in,
          will honor STOP/HELP keywords, and will not send marketing content over this channel.
          Acknowledging does not enable SMS — carrier approval is still required.
        </p>
        {c.termsAcknowledgedAt && (
          <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            Acknowledged on {new Date(c.termsAcknowledgedAt).toLocaleString()}.
          </p>
        )}
        <div>
          <Button onClick={() => void handleAcknowledge()} disabled={acknowledge.isPending}>
            {acknowledge.isPending
              ? 'Recording…'
              : c.termsAcknowledgedAt
                ? 'Re-acknowledge'
                : 'Acknowledge terms'}
          </Button>
        </div>
      </section>

      <section aria-labelledby="consent-heading" className="surface-elevated space-y-4 p-6">
        <header className="flex items-baseline justify-between gap-3">
          <h2 id="consent-heading" className="font-[var(--font-display)] text-[var(--text-xl)]">
            Record a recipient&rsquo;s consent
          </h2>
          <Badge tone="neutral">{c.optedInCount} opted in</Badge>
        </header>
        <p className="max-w-prose text-[var(--color-ink-muted)]">
          When a family gives permission to text them &mdash; an enrollment-form checkbox, a signed
          form, or a verbal yes at the desk &mdash; record it here. The source you enter is kept as
          your consent record. Recording consent does not send anything or enable SMS.
        </p>
        <form onSubmit={(e) => void handleRecordConsent(e)} className="grid gap-4 sm:max-w-lg">
          <div>
            <Label htmlFor="consent-phone">Recipient phone</Label>
            <Input
              id="consent-phone"
              type="tel"
              inputMode="tel"
              placeholder="+15555550123"
              autoComplete="off"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="consent-source">How consent was obtained</Label>
            <Input
              id="consent-source"
              placeholder="e.g. 2026 enrollment form checkbox"
              autoComplete="off"
              value={source}
              onChange={(e) => setSource(e.target.value)}
            />
          </div>
          <fieldset className="flex flex-wrap gap-4">
            <legend className="sr-only">Consent type</legend>
            <label className="flex items-center gap-2 text-[var(--text-sm)]">
              <input
                type="radio"
                name="consent-type"
                checked={optIn}
                onChange={() => setOptIn(true)}
              />
              Opt in (permission given)
            </label>
            <label className="flex items-center gap-2 text-[var(--text-sm)]">
              <input
                type="radio"
                name="consent-type"
                checked={!optIn}
                onChange={() => setOptIn(false)}
              />
              Opt out (do not text)
            </label>
          </fieldset>
          <div>
            <Button type="submit" disabled={recordConsent.isPending}>
              {recordConsent.isPending ? 'Recording…' : optIn ? 'Record opt-in' : 'Record opt-out'}
            </Button>
          </div>
        </form>
      </section>

      <p className="max-w-prose text-[var(--color-ink-muted)] text-[var(--text-sm)]">
        A2P 10DLC brand and campaign registration is handled with the carriers outside this app. SMS
        remains blocked at the server until that registration is approved, regardless of the
        settings shown here.
      </p>
    </div>
  );
}
